import { all, call, put, select, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import { ITourProgress } from '../../../models';
import { getServiceProvider, IServiceProvider } from '../../../services';
import { commonActions } from '../../common';
import { requireUserOrThrow } from '../../utils/sagas';
import actions from '../actions';
import selectors from '../selectors';

const saveTourProgressSaga = takeLatest(
  [actions.saveTourProgress.request.type, commonActions.cancelTour.confirmed.type, actions.completeTour.success.type],
  function* (
    action:
      | ReturnType<typeof actions.saveTourProgress.request>
      | ReturnType<typeof commonActions.cancelTour.confirmed>
      | ReturnType<typeof actions.completeTour.success>
  ) {
    const tourCode = typeof action.payload === 'string' ? action.payload : action.payload.tourCode;

    const tourProgress: ITourProgress | undefined = yield select(selectors.selectTourProgressByTourCode, {
      tour: tourCode,
    });
    if (tourProgress) {
      const serviceProvider: IServiceProvider = yield getServiceProvider();
      try {
        yield requireUserOrThrow();
        const savedTourProgress = yield serviceProvider.api.saveTourProgress(tourProgress);
        yield put(actions.saveTourProgress.success(savedTourProgress));
      } catch (e) {
        yield put(actions.saveTourProgress.failure(e));
      }
    } else {
      yield put(actions.saveTourProgress.cancel());
    }
  }
);

const triggerTourProgressSync = takeEvery(
  [commonActions.connection.online.type, commonActions.signIn.success.type],
  function* () {
    yield put(commonActions.synchronizeTourProgress.request());
  }
);

const synchronizeTourProgressesSaga = takeLeading([commonActions.synchronizeTourProgress.request.type], function* () {
  const serviceProvider: IServiceProvider = yield getServiceProvider();

  function* saveTourProgress(tp: ITourProgress) {
    yield serviceProvider.api.saveTourProgress(tp);
    yield put(actions.saveTourProgress.success(tp));
  }

  const unsynchronizedTourProgresses: ITourProgress[] = yield select(selectors.selectUnsynchronizedTourProgresses);
  const pendingSaves = unsynchronizedTourProgresses.map(tourProcess => call(saveTourProgress, tourProcess));
  try {
    yield all(pendingSaves);
    const tourProgresses = yield serviceProvider.api.fetchTourProgresses();
    yield put(commonActions.synchronizeTourProgress.success(tourProgresses));
  } catch (e) {
    yield put(commonActions.synchronizeTourProgress.failure(e));
  }
});

const sagas = [triggerTourProgressSync, saveTourProgressSaga, synchronizeTourProgressesSaga];

export default sagas;
